import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface GlobalErrorEntry {
  kind: "error" | "unhandledrejection";
  message: string;
  source?: string;
  detail?: string;
}

interface State {
  error: unknown;
  info: ErrorInfo | null;
  globals: GlobalErrorEntry[];
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, info: null, globals: [] };

  private onError = (e: ErrorEvent) => {
    this.setState((s) => ({
      globals: [
        ...s.globals,
        {
          kind: "error",
          message: e.message || "(no message)",
          source: e.filename ? `${e.filename}:${e.lineno}:${e.colno}` : undefined,
          detail: e.error ? describe(e.error) : undefined
        }
      ]
    }));
  };

  private onRejection = (e: PromiseRejectionEvent) => {
    this.setState((s) => ({
      globals: [
        ...s.globals,
        {
          kind: "unhandledrejection",
          message: describe(e.reason)
        }
      ]
    }));
  };

  componentDidMount() {
    window.addEventListener("error", this.onError);
    window.addEventListener("unhandledrejection", this.onRejection);
  }

  componentWillUnmount() {
    window.removeEventListener("error", this.onError);
    window.removeEventListener("unhandledrejection", this.onRejection);
  }

  static getDerivedStateFromError(error: unknown): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("ErrorBoundary caught:", error, info);
    this.setState({ info });
  }

  render() {
    const { error, info, globals } = this.state;
    const hasReactError = error !== null;
    if (!hasReactError && globals.length === 0) {
      return this.props.children;
    }
    return (
      <>
        {hasReactError && (
          <div className="p-4 space-y-3 max-w-md mx-auto">
            <div className="p-3 rounded-lg bg-red-950 border border-red-800 text-red-200 space-y-2">
              <div className="font-semibold">App crashed</div>
              <pre className="text-xs whitespace-pre-wrap break-all">{describe(error)}</pre>
              {info?.componentStack && (
                <details>
                  <summary className="text-xs cursor-pointer">Component stack</summary>
                  <pre className="text-[10px] mt-1 whitespace-pre-wrap break-all text-red-300/80">
                    {info.componentStack}
                  </pre>
                </details>
              )}
            </div>
            <button
              onClick={() => this.setState({ error: null, info: null })}
              className="w-full py-3 rounded-lg bg-slate-800 text-slate-100"
            >
              Reset
            </button>
            {globals.length > 0 && <GlobalsPanel globals={globals} onClear={() => this.setState({ globals: [] })} />}
            {this.props.children}
          </div>
        )}
        {!hasReactError && globals.length > 0 && (
          <>
            <GlobalsPanel globals={globals} onClear={() => this.setState({ globals: [] })} />
            {this.props.children}
          </>
        )}
      </>
    );
  }
}

function GlobalsPanel({ globals, onClear }: { globals: GlobalErrorEntry[]; onClear: () => void }) {
  return (
    <div className="fixed bottom-20 inset-x-0 mx-auto max-w-md px-4 z-50">
      <div className="p-3 rounded-lg bg-amber-950 border border-amber-800 text-amber-100 text-xs space-y-2 max-h-64 overflow-auto">
        <div className="flex justify-between items-center">
          <span className="font-semibold">Global errors ({globals.length})</span>
          <button onClick={onClear} className="text-amber-400 underline">clear</button>
        </div>
        {globals.map((g, i) => (
          <div key={i} className="border-t border-amber-800/50 pt-1">
            <div className="font-mono">{g.kind}: {g.message}</div>
            {g.source && <div className="font-mono opacity-70">{g.source}</div>}
            {g.detail && <pre className="whitespace-pre-wrap break-all opacity-70">{g.detail}</pre>}
          </div>
        ))}
      </div>
    </div>
  );
}

function describe(err: unknown): string {
  if (err == null) return `(thrown ${String(err)})`;
  if (typeof err !== "object") return `${typeof err}: ${String(err)}`;
  const parts: string[] = [];
  const e = err as Record<string, unknown>;
  const ctor = (err as object).constructor?.name;
  if (ctor) parts.push(`constructor: ${ctor}`);
  if (typeof e.name === "string") parts.push(`name: ${e.name}`);
  if (typeof e.message === "string") parts.push(`message: ${e.message || "(empty)"}`);
  if (typeof e.code !== "undefined") parts.push(`code: ${String(e.code)}`);
  const allKeys = Object.getOwnPropertyNames(e);
  const extra = allKeys.filter((k) => !["name", "message", "stack", "code", "constructor"].includes(k));
  for (const k of extra) {
    try {
      const v = e[k];
      const s = typeof v === "string" ? v : JSON.stringify(v);
      parts.push(`${k}: ${s}`);
    } catch {
      parts.push(`${k}: (unserializable)`);
    }
  }
  if (typeof e.stack === "string") parts.push(`stack:\n${e.stack}`);
  let stringified = "";
  try {
    stringified = JSON.stringify(err);
    if (stringified && stringified !== "{}") parts.push(`json: ${stringified}`);
  } catch {
    // ignore
  }
  return parts.join("\n");
}
